import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { cacheService } from "../../../Core/Api/services";
import { DragHandleOutlined } from "@mui/icons-material";
import { Box, SxProps } from "@mui/material";

export interface DragOrderedChildrenProps {
  children: React.ReactElement[];
  onDragEnd?: () => void;
  onDragStart?: () => void;
  cacheListKey?: string;
  callback?: (order: string[]) => void;
  dragIconHandle?: boolean;
  dragIconSx?: SxProps;
}

const DragOrderedChildren: React.FC<DragOrderedChildrenProps> = (props) => {
  const { children, onDragEnd, onDragStart, cacheListKey, dragIconHandle = false, dragIconSx = {}, callback } = props;
  const [localChildren, setLocalChildren] = useState<string[]>(
    cacheListKey ? [] : children.map((x) => x.key! as string)
  );

  useEffect(() => {
    if (!cacheListKey) return;
    const order = cacheService.GeDragOrderListByKey(cacheListKey);
    let localOrder: string[] = [];
    order.forEach((x) => {
      if (children.findIndex((a) => a.key === x) > -1) localOrder.push(x);
    });
    children.forEach((x) => {
      if (localOrder.findIndex((a) => a === x.key) === -1) localOrder.push((x.key ?? -1).toString());
    });
    setLocalChildren(localOrder);
  }, [cacheListKey, children.length]);

  useEffect(() => {
    !cacheListKey && setLocalChildren(children.map((x) => x.key! as string));
  }, [children.length]);

  function reorderChildren(result) {
    onDragEnd && onDragEnd();
    const startIndex = result.source?.index;
    const endIndex = result.destination?.index;

    startIndex !== undefined &&
      endIndex !== undefined &&
      startIndex !== endIndex &&
      setLocalChildren((x) => {
        const tempChildren = [...x];
        const [removed] = tempChildren.splice(startIndex, 1);
        tempChildren.splice(endIndex, 0, removed);
        cacheListKey && cacheService.SetDragListOrder(cacheListKey, tempChildren);
        callback && callback(tempChildren);
        return tempChildren;
      });
  }

  if (children.length <= 1) return <>{children}</>;

  return (
    <DragDropContext onDragStart={() => onDragStart && onDragStart()} onDragEnd={reorderChildren}>
      <Droppable droppableId={cacheListKey || "default-drop-id-no-cache"}>
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{
              //...provided.droppableProps.style,
              maxWidth: "100%",
            }}
          >
            {[...children]
              .sort(
                (a, b) =>
                  localChildren.indexOf((a.key ?? -1).toString()) - localChildren.indexOf((b.key ?? -1).toString())
              )
              .map((child, i) => (
                <Draggable key={i} draggableId={i.toString()} index={i}>
                  {(provided, snapshot) =>
                    dragIconHandle ? (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          position: "relative",
                          flexDirection: "row",
                          display: "flex",
                          width: "100%",
                          ...provided.draggableProps.style,
                        }}
                      >
                        <Box flex={20}>{child}</Box>
                        <Box
                          {...provided.dragHandleProps}
                          flex={1}
                          sx={{
                            //...provided.dragHandleProps.style,
                            position: "relative",
                            ...dragIconSx,
                          }}
                        >
                          <DragHandleOutlined color="disabled" fontSize="large" />
                        </Box>
                      </Box>
                    ) : (
                      <div ref={provided.innerRef} {...provided.dragHandleProps} {...provided.draggableProps}>
                        {child}
                      </div>
                    )
                  }
                </Draggable>
              ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DragOrderedChildren;
